import { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Check, MessageCircle, Lock } from "lucide-react";
import { PACKAGES, PROFILE } from "@/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageCard = ({ pkg, config }) => {
  const createOrder = async () => {
    const res = await axios.post(`${API}/paypal/orders`, {
      package_id: pkg.id,
      amount: pkg.price,
      description: `${pkg.name} — MD Nurul Islam Sumon`,
    });
    return res.data.id;
  };

  const onApprove = async (data) => {
    try {
      const res = await axios.post(`${API}/paypal/orders/${data.orderID}/capture`);
      if (res.data.status === "COMPLETED") {
        toast.success(`Payment received for ${pkg.name} — Sumon will contact you within 24 hours`);
      } else {
        toast.info("Payment processing — confirmation coming shortly");
      }
    } catch {
      toast.error("Could not confirm payment — message Sumon on WhatsApp with your order ID");
    }
  };

  return (
    <div
      data-testid={`card-package-${pkg.id}`}
      className={`relative rounded-2xl border p-8 flex flex-col transition-all duration-500 hover:scale-[1.02] ${
        pkg.featured
          ? "border-[#D9A441]/60 bg-[#101F38] shadow-xl shadow-[#D9A441]/10"
          : "border-white/10 bg-[#101F38]/60 hover:border-white/25"
      }`}
    >
      {pkg.featured && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-xs font-mono uppercase tracking-widest bg-[#D9A441] text-[#0A1730] font-bold rounded-full px-4 py-1.5">
          Most Popular
        </span>
      )}
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D9A441]">{pkg.name}</p>
      <p className="mt-4 font-display text-4xl font-bold text-[#F3EFE6]">
        ${pkg.price}
        <span className="text-sm font-sans font-normal text-[#9DAEC4]"> /project</span>
      </p>
      <p className="mt-2 text-sm text-[#9DAEC4]">{pkg.tagline}</p>
      <ul className="mt-6 space-y-3 flex-1">
        {pkg.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#C8D3E0]">
            <Check size={15} className="text-emerald-400 mt-0.5 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      <div className="mt-8" data-testid={`paypal-button-${pkg.id}`}>
        {config?.enabled ? (
          <PayPalButtons
            style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 45 }}
            createOrder={createOrder}
            onApprove={onApprove}
          />
        ) : (
          <a
            href={PROFILE.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`btn-book-whatsapp-${pkg.id}`}
            className="flex items-center justify-center gap-2 w-full bg-[#D9A441] hover:bg-[#E5B85C] text-[#0A1730] font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03]"
          >
            <MessageCircle size={16} />
            Book on WhatsApp
          </a>
        )}
      </div>
    </div>
  );
};

export default function Packages() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/paypal/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  const grid = (
    <div className="grid md:grid-cols-3 gap-6 items-stretch">
      {PACKAGES.map((pkg, i) => (
        <Reveal key={pkg.id} delay={i * 0.1} className="h-full">
          <PackageCard pkg={pkg} config={config} />
        </Reveal>
      ))}
    </div>
  );

  return (
    <section id="packages" data-testid="packages-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 right-1/4 w-[26rem] h-[26rem] rounded-full bg-[#D9A441]/10 blur-[130px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading id="packages" overline="Packages & Payment" title="Pick a package, pay securely" />
        <Reveal className="mb-10 flex items-center gap-2.5 text-sm text-[#9DAEC4]">
          <Lock size={14} className="text-emerald-400" />
          Secure checkout via PayPal — card payments accepted, no PayPal account needed.
        </Reveal>
        {config?.enabled ? (
          <PayPalScriptProvider options={{ "client-id": config.clientId, currency: "USD" }}>
            {grid}
          </PayPalScriptProvider>
        ) : (
          grid
        )}
      </div>
    </section>
  );
}
